import React, { useEffect, useMemo, useState } from "react";
import { fechaMX } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const kg = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Inventario() {
  const { isAdmin } = useAuth();
  const [entradas, setEntradas] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ fecha: "", remision_proveedor: "", rollos: "", kg: "" });
  const [umbral, setUmbral] = useState(3000);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [e1, e2, p] = await Promise.all([api.get("/inventario/entradas"), api.get("/inventario/entregas"), api.get("/parametros")]);
      setEntradas(e1.data || []);
      setEntregas(e2.data || []);
      setUmbral(Number(p.data?.umbral_stock_bajo ?? 3000));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const agregarEntrada = async () => {
    if (!form.fecha || !form.kg) return toast.error("Complete al menos Fecha y Kg");
    try {
      await api.post("/inventario/entradas", {
        fecha: form.fecha,
        remision_proveedor: form.remision_proveedor || null,
        rollos: Number(form.rollos || 0),
        kg: Number(form.kg || 0),
      });
      setForm({ fecha: "", remision_proveedor: "", rollos: "", kg: "" });
      toast.success("Entrada de material registrada");
      fetchAll();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminarEntrada = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada de material?")) return;
    try { await api.delete(`/inventario/entradas/${id}`); toast.success("Entrada eliminada"); fetchAll(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const totalRecibidos = entradas.reduce((a, e) => a + Number(e.kg || 0), 0);
  const totalEntregados = entregas.reduce((a, s) => a + Number(s.kg || 0), 0);
  const kgDisponibles = totalRecibidos - totalEntregados;
  const bajoStock = kgDisponibles <= umbral;

  // Kardex: movimientos con saldo corrido GLOBAL (cronológico ascendente).
  const movimientos = useMemo(() => {
    const ent = entradas.map((e) => ({ id: e.id, tipo: "ENTRADA", fecha: e.fecha, ref: e.remision_proveedor || "Compra de material", rollos: Number(e.rollos || 0), kg: Number(e.kg || 0) }));
    const sal = entregas.map((s) => ({ id: s.id, tipo: "SALIDA", fecha: s.fecha, ref: `${s.numero_remision || "—"} · ${s.cliente || ""}`.trim(), kg: Number(s.kg || 0) }));
    const all = [...ent, ...sal];
    const key = (m) => `${m.fecha || "9999-99-99"}|${m.tipo === "ENTRADA" ? 0 : 1}`;
    const asc = [...all].sort((a, b) => key(a).localeCompare(key(b)));
    let saldo = 0;
    asc.forEach((m) => { saldo += m.tipo === "ENTRADA" ? m.kg : -m.kg; m.saldo = saldo; });
    return all;
  }, [entradas, entregas]);

  const grupos = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth();
    const s = search.toLowerCase();
    const filtrados = movimientos.filter((m) => !s || `${m.tipo} ${m.ref} ${m.fecha || ""}`.toLowerCase().includes(s));
    const sortDesc = (a, b) => (b.fecha || "").localeCompare(a.fecha || "");
    const mesEnCurso = [], mesesAnio = {}, porAnio = {}, sinFecha = [];
    filtrados.forEach((m) => {
      const d = m.fecha ? new Date(m.fecha + "T00:00:00") : null;
      if (!d || isNaN(d)) { sinFecha.push(m); return; }
      const y = d.getFullYear(), mo = d.getMonth();
      if (y === curY && mo === curM) mesEnCurso.push(m);
      else if (y === curY) (mesesAnio[mo] = mesesAnio[mo] || []).push(m);
      else if (y < curY) { (porAnio[y] = porAnio[y] || {}); (porAnio[y][mo] = porAnio[y][mo] || []).push(m); }
      else (mesesAnio[mo] = mesesAnio[mo] || []).push(m);
    });
    mesEnCurso.sort(sortDesc); sinFecha.sort(sortDesc);
    Object.values(mesesAnio).forEach((a) => a.sort(sortDesc));
    Object.values(porAnio).forEach((mm) => Object.values(mm).forEach((a) => a.sort(sortDesc)));
    return { mesEnCurso, mesesAnio, porAnio, sinFecha, curY };
  }, [movimientos, search]);
  const mesesAnioKeys = Object.keys(grupos.mesesAnio).map(Number).sort((a, b) => a - b);
  const countMesesAnio = mesesAnioKeys.reduce((s, m) => s + grupos.mesesAnio[m].length, 0);
  const aniosKeys = Object.keys(grupos.porAnio).sort((a, b) => String(b).localeCompare(String(a)));
  const countAnios = aniosKeys.reduce((s, y) => s + Object.values(grupos.porAnio[y]).reduce((t, a) => t + a.length, 0), 0);

  const Seccion = ({ titulo, rows }) => (
    <div className="space-y-2" data-testid={`inv-seccion-${titulo.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{titulo}</h3>
        <Badge variant="secondary">{rows.length}</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground pb-2">Sin movimientos en este periodo.</p>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Movimiento</TableHead><TableHead>Referencia</TableHead><TableHead className="text-right">Entrada</TableHead><TableHead className="text-right">Salida</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={`${m.tipo}-${m.id}`} data-testid={`inv-mov-${m.id}`}>
                  <TableCell>{fechaMX(m.fecha, "—")}</TableCell>
                  <TableCell>
                    {m.tipo === "ENTRADA"
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent">Entrada</Badge>
                      : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent">Salida</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {m.ref}
                    {m.tipo === "ENTRADA" && m.rollos > 0 && <span className="text-muted-foreground"> · {m.rollos} rollos</span>}
                  </TableCell>
                  <TableCell className="text-right text-emerald-700 font-medium">{m.tipo === "ENTRADA" ? `+${kg(m.kg)}` : "—"}</TableCell>
                  <TableCell className="text-right text-red-600 font-medium">{m.tipo === "SALIDA" ? `−${kg(m.kg)}` : "—"}</TableCell>
                  <TableCell className={`text-right font-semibold ${m.saldo < 0 ? "text-red-600" : ""}`}>{kg(m.saldo)}</TableCell>
                  <TableCell>
                    {m.tipo === "ENTRADA" && isAdmin && (
                      <Button variant="ghost" size="icon" className="text-destructive" title="Eliminar entrada" onClick={() => eliminarEntrada(m.id)} data-testid={`inv-del-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="inventario-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Inventario</h1><p className="text-muted-foreground mt-2">Kardex de material en kg: entradas de compra y salidas por pedidos entregados, con saldo corrido.</p></div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Kg Recibidos</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold" data-testid="inv-recibidos">{kg(totalRecibidos)} kg</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Kg Entregados</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold" data-testid="inv-entregados">{kg(totalEntregados)} kg</div></CardContent></Card>
        <Card className={bajoStock ? "border-red-300" : ""}><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Kg Disponibles</CardTitle></CardHeader><CardContent><div className={`text-2xl font-bold ${kgDisponibles < 0 ? "text-red-600" : bajoStock ? "text-amber-600" : ""}`} data-testid="inv-disponibles">{kg(kgDisponibles)} kg</div></CardContent></Card>
      </div>

      {bajoStock && !loading && (
        <div className="flex items-center gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-700" data-testid="inv-alerta-bajo">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="text-sm font-medium">Inventario bajo: quedan {kg(kgDisponibles)} kg (umbral {kg(umbral)} kg). Considera recargar material.</span>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Registrar entrada de material</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="space-y-2 flex-1"><Label htmlFor="fecha">Fecha</Label><Input id="fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="inv-fecha-input" /></div>
            <div className="space-y-2 flex-1"><Label htmlFor="remprov">Remisión proveedor</Label><Input id="remprov" value={form.remision_proveedor} onChange={(e) => setForm({ ...form, remision_proveedor: e.target.value })} placeholder="Folio del proveedor" data-testid="inv-remprov-input" /></div>
            <div className="space-y-2 w-full md:w-32"><Label htmlFor="rollos">Rollos</Label><Input id="rollos" type="number" min="0" value={form.rollos} onChange={(e) => setForm({ ...form, rollos: e.target.value })} data-testid="inv-rollos-input" /></div>
            <div className="space-y-2 w-full md:w-40"><Label htmlFor="kgin">Kg Recibidos</Label><Input id="kgin" type="number" min="0" step="0.01" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })} data-testid="inv-kg-input" /></div>
            <Button onClick={agregarEntrada} data-testid="inv-add-entrada"><Plus className="h-4 w-4 mr-2" /> Agregar</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Movimientos (Kardex)</CardTitle>
          <div className="relative max-w-lg mt-2">
            <Input type="search" placeholder="Buscar por tipo, referencia o fecha..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inv-search" />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Cargando...</p>
          ) : (
            <>
              <Seccion titulo="Mes en curso" rows={grupos.mesEnCurso} />

              <Accordion type="multiple" className="border rounded-md divide-y">
                <AccordionItem value="meses-anio" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-meses-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Meses anteriores ({grupos.curY})</span><Badge variant="secondary">{countMesesAnio}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    {mesesAnioKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sin movimientos en meses anteriores de este año.</p>
                    ) : (
                      mesesAnioKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${grupos.curY}`} rows={grupos.mesesAnio[m]} />)
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="anios" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-anios-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Años anteriores</span><Badge variant="secondary">{countAnios}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {aniosKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sin movimientos de años anteriores.</p>
                    ) : (
                      <Accordion type="multiple" className="border rounded-md divide-y">
                        {aniosKeys.map((y) => {
                          const mesesKeys = Object.keys(grupos.porAnio[y]).map(Number).sort((a, b) => a - b);
                          const countY = mesesKeys.reduce((s, m) => s + grupos.porAnio[y][m].length, 0);
                          return (
                            <AccordionItem key={y} value={y} className="border-0 px-4">
                              <AccordionTrigger data-testid={`acc-anio-${y}`} className="hover:no-underline">
                                <span className="flex items-center gap-2"><span className="font-semibold">{y}</span><Badge variant="secondary">{countY}</Badge></span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-6 pt-2">
                                {mesesKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${y}`} rows={grupos.porAnio[y][m]} />)}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {grupos.sinFecha.length > 0 && <Seccion titulo="Sin fecha" rows={grupos.sinFecha} />}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
